import React, { memo, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AppProvider } from './context/AppContext';
import { Box, CircularProgress } from '@mui/material';
import lazyLoad from './utils/lazyLoad';

// Lazy-loaded components with better chunk naming
const HomePage = lazyLoad('pages/HomePage');
const ScorePage = lazyLoad('pages/ScorePage');
const AboutPage = lazyLoad('pages/AboutPage');
const FAQPage = lazyLoad('pages/FaqPage'); // Lazy load the FAQ page
const PlaceholderPage = lazyLoad('pages/PlaceholderPage'); // Lazy load placeholder page
const LoginPage = lazyLoad('pages/LoginPage'); // Lazy load the login page
const MethodologyPage = lazyLoad('pages/MethodologyPage'); // Lazy load the methodology page
const DogResourcesPage = lazyLoad('pages/DogResourcesPage'); // Lazy load the dog resources page
// Add RegisterPage and ProfilePage when they are created
// const RegisterPage = lazyLoad('pages/RegisterPage');
// const ProfilePage = lazyLoad('pages/ProfilePage');

/**
 * Loading component shown while lazy-loaded components are being loaded
 */
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh' 
    }}
  >
    <CircularProgress color="primary" />
  </Box>
);

/**
 * Root Application Component
 * 
 * Sets up the main application structure including:
 * - Material UI ThemeProvider for consistent styling
 * - CssBaseline for consistent base styles
 * - React Router for navigation between pages
 * - ErrorBoundary for graceful error handling
 * - AppProvider for global state management
 * - Suspense for code splitting and lazy loading
 * 
 * This component should remain relatively simple as it serves
 * primarily as the application's entry point and router configuration.
 */
const App = memo(() => {
  return (
    <ErrorBoundary>
      <AppProvider>
        <ThemeProvider theme={theme}>
          {/* CssBaseline normalizes styles across browsers */}
          <CssBaseline />
          
          <Router>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Main application routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/scoringsample" element={<ScorePage />} />
                <Route path="/AboutUs" element={<AboutPage />} />
                
                {/* Login page route */}
                <Route path="/login" element={<LoginPage />} />
                
                {/* Methodology page route */}
                <Route path="/OurPurpose" element={<MethodologyPage />} />

                {/* Dog Resources page route */}
                <Route path="/dog-resources" element={<DogResourcesPage />} />

                {/* FAQ page route */}
                <Route path="/faq" element={<FAQPage />} />
                
                {/* Placeholder routes for now */}
                <Route path="/register" element={<PlaceholderPage />} />
                <Route path="/profile" element={<PlaceholderPage />} />
                <Route path="/widget" element={<PlaceholderPage />} />
                
                {/* Routes for undeveloped features -> PlaceholderPage */}
                <Route path="/blog" element={<PlaceholderPage />} />
                <Route path="/contact" element={<PlaceholderPage />} />
                <Route path="/add-to-site" element={<PlaceholderPage />} />
                <Route path="/api" element={<PlaceholderPage />} />
                <Route path="/privacy" element={<PlaceholderPage />} />
                <Route path="/terms" element={<PlaceholderPage />} />
                
                {/* Consider adding a 404 route eventually */}
                <Route path="*" element={<PlaceholderPage title="Page Not Found" message="Oops! We couldn't find the page you were looking for." />} /> 
              </Routes>
            </Suspense>
          </Router>
        </ThemeProvider>
      </AppProvider>
    </ErrorBoundary>
  );
});

// For dev tools display name
App.displayName = 'App';

export default App;
